import { useEffect, useMemo, useState } from "react";
import {
  Footprints,
  Shirt,
  Sun,
  Glasses,
  Droplet,
  ShieldOff,
  Smile,
  Bath,
  Lock,
  Smartphone,
  Gem,
  Wallet,
  Download,
  CheckCircle,
  XCircle,
  Mail,
} from "lucide-react";
import { business } from "../data/business";

// Storage key for the interactive checklist (#56). Stored as a string[] of
// item ids — narrow enough that even private-browsing failure modes only
// cost the user their checked state for this session.
const STORAGE_KEY = "ocoee.packing.checked";

// General whitewater best-practice checklist — softened to avoid asserting
// venue-specific policies (lockers, no-glass rule, refill stations, "we
// don't provide towels") that the business has not confirmed in writing.
//
// `why` is an OPTIONAL safety-rationale we can defend from first principles
// (open shoes get pulled off in rapids, loose glasses sink, glass shatters
// on rocks). If we can't justify a reason without inventing one we omit
// the field — the <details> Why? block only renders when `why` is present.
type Item = {
  id: string;
  icon: typeof Footprints;
  label: string;
  note: string;
  why?: string;
};

const bring: Item[] = [
  {
    id: "bring-shoes",
    icon: Footprints,
    label: "Closed-toe shoes",
    note: "Water shoes or old sneakers — leave the flip-flops at home",
    why: "Open shoes get pulled off in the rapids and rocks bruise bare feet on the bank.",
  },
  {
    id: "bring-dry-clothes",
    icon: Shirt,
    label: "Change of dry clothes",
    note: "Quick-dry layers · you will get wet",
    why: "You finish the run soaked; dry layers are the difference between a comfortable ride home and a cold one.",
  },
  {
    id: "bring-towel",
    icon: Bath,
    label: "A towel",
    note: "For after the trip",
  },
  {
    id: "bring-sunscreen",
    icon: Sun,
    label: "Sunscreen",
    note: "Apply before launch · reef-safe preferred",
    why: "The river reflects a lot of sun and you can't reapply mid-rapid.",
  },
  {
    id: "bring-lipbalm",
    icon: Smile,
    label: "Lip balm + cap",
    note: "SPF lip balm and a snug cap or visor",
  },
  {
    id: "bring-strap",
    icon: Glasses,
    label: "Sunglass strap",
    note: "Loose glasses end up in the river",
    why: "A swim flips the boat or your head; without a strap the glasses are gone.",
  },
  {
    id: "bring-water",
    icon: Droplet,
    label: "Water bottle",
    note: "Reusable, ideally non-glass",
    why: "Glass shatters on rocks — keep it metal or plastic.",
  },
];

const leave: Item[] = [
  {
    id: "leave-phones",
    icon: Smartphone,
    label: "Phones",
    note: "Or use a head/chest mount if you want to film",
    why: "A handheld phone in Class III+ water becomes a phone-shaped rock at the bottom of the river.",
  },
  {
    id: "leave-jewelry",
    icon: Gem,
    label: "Jewelry",
    note: "Rings, earrings, bracelets — easy to lose in a swim",
    why: "Cold water shrinks fingers and a swim shakes loose anything not bolted down.",
  },
  {
    id: "leave-wallets",
    icon: Wallet,
    label: "Wallets",
    note: "Bring only what you need · ask at check-in about secure storage",
  },
  {
    id: "leave-watches",
    icon: Lock,
    label: "Watches",
    note: "Even waterproof — easy to lose in a swim",
    why: "Waterproof ≠ stays-on-your-wrist. A swim shakes loose anything with a clasp.",
  },
];

function readChecked(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((v) => typeof v === "string");
  } catch {
    return [];
  }
}

function writeChecked(ids: string[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  } catch {
    /* private-browsing or quota — silently ignore */
  }
}

export function WhatToBring() {
  const [checked, setChecked] = useState<string[]>([]);

  // Restore checkbox state on mount (#56).
  useEffect(() => {
    setChecked(readChecked());
  }, []);

  const toggle = (id: string) => {
    setChecked((prev) => {
      const next = prev.includes(id)
        ? prev.filter((v) => v !== id)
        : [...prev, id];
      writeChecked(next);
      return next;
    });
  };

  const total = bring.length + leave.length;
  const checkedCount = checked.length;

  // mailto body (#57) — plain-text dump of both lists.
  const mailtoHref = useMemo(() => {
    const lines: string[] = [];
    lines.push("BRING:");
    bring.forEach((b) => lines.push(`- ${b.label} (${b.note})`));
    lines.push("");
    lines.push("LEAVE BEHIND:");
    leave.forEach((l) => lines.push(`- ${l.label} (${l.note})`));
    lines.push("");
    lines.push(`— ${business.name}`);
    const subject = encodeURIComponent("My Ocoee packing list");
    const body = encodeURIComponent(lines.join("\n"));
    return `mailto:?subject=${subject}&body=${body}`;
  }, []);

  const progressPct = total > 0 ? Math.round((checkedCount / total) * 100) : 0;

  return (
    <section className="bring" aria-label="What to bring">
      <div className="bring-head">
        <span className="eye">WHAT TO BRING</span>
        <h2 className="h2">What to bring on the river</h2>
        <p className="body">
          Helmet, life jacket, paddle, and wetsuit (when cold) are all
          included. Here's what to bring — and what to leave behind.
        </p>
        {/* New round #53 */}
        <a className="bring-print" href="/print/what-to-bring.pdf" target="_blank" rel="noopener noreferrer">
          <Download size={14} aria-hidden="true" /> Printable PDF checklist
        </a>

        {/* #58 — progress bar (hidden when nothing checked) */}
        {checkedCount > 0 && (
          <div className="bring-progress" aria-live="polite">
            <div
              className="bring-progress-bar"
              role="progressbar"
              aria-valuenow={checkedCount}
              aria-valuemin={0}
              aria-valuemax={total}
            >
              <span
                className="bring-progress-fill"
                style={{ width: `${progressPct}%` }}
              />
            </div>
            <span className="bring-progress-label">
              {checkedCount}/{total} items packed
            </span>
          </div>
        )}
      </div>

      <div className="bring-grid">
        <div className="bring-col">
          <h3 className="bring-col-head bring-col-head-on">
            <ShieldOff style={{ display: "none" }} />
            BRING
          </h3>
          <ul className="bring-list bring-list-2col">
            {bring.map(({ id, icon: Icon, label, note, why }) => {
              const isChecked = checked.includes(id);
              return (
                <li
                  key={id}
                  className={`bring-item ${isChecked ? "is-checked" : ""}`}
                >
                  <span className="bring-icon" aria-hidden="true">
                    <Icon />
                  </span>
                  <div className="bring-body">
                    <label className="bring-check">
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => toggle(id)}
                      />
                      <CheckCircle
                        size={16}
                        aria-hidden="true"
                        className="bring-status bring-status-on"
                      />
                      <strong>{label}</strong>
                    </label>
                    <span className="bring-note">{note}</span>
                    {why && (
                      <details className="bring-why">
                        <summary>Why?</summary>
                        <p>{why}</p>
                      </details>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="bring-col">
          <h3 className="bring-col-head bring-col-head-off">LEAVE BEHIND</h3>
          <ul className="bring-list bring-list-2col">
            {leave.map(({ id, icon: Icon, label, note, why }) => {
              const isChecked = checked.includes(id);
              return (
                <li
                  key={id}
                  className={`bring-item bring-item-off ${isChecked ? "is-checked" : ""}`}
                >
                  <span className="bring-icon bring-icon-off" aria-hidden="true">
                    <Icon />
                  </span>
                  <div className="bring-body">
                    <label className="bring-check">
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => toggle(id)}
                      />
                      <XCircle
                        size={16}
                        aria-hidden="true"
                        className="bring-status bring-status-off"
                      />
                      <strong>{label}</strong>
                    </label>
                    <span className="bring-note">{note}</span>
                    {why && (
                      <details className="bring-why">
                        <summary>Why?</summary>
                        <p>{why}</p>
                      </details>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      {/* #57 — Email this list */}
      <div className="bring-foot">
        <a className="bring-email" href={mailtoHref}>
          <Mail size={14} aria-hidden="true" /> Email me this list
        </a>
      </div>
    </section>
  );
}
